import { useEffect, useRef, useState } from "react";

// What the pointer is over on a computer, a row of the map's list, a pin on the map, or the small card a
// rest on either shows at the map's foot (features/map.md): each side lights its own (the pin of a
// hovered row, the row of a hovered pin) straight in the page, without React drawing the list or the
// map again, so sweeping down hundreds of rows stays smooth.

// Where the pointer can hover: a mouse, a trackpad or a pen, on a computer or a tablet. Touch never hovers.
export const HOVERS = "(hover: hover) and (pointer: fine)";

export type HoverFrom = "list" | "map" | "card";

// How long the pointer rests on a row of the list or a pin before its small card shows (and, for a row
// whose place is off screen, before the map zooms out to show it): longer than a sweep down the list
// spends on each row, so a sweep never flashes a card. And how long it's off them before the card goes
// and the map comes back, so slipping off an edge doesn't bounce.
export const REST_MS = 400;
export const AWAY_MS = 400;

type Timer = ReturnType<typeof setTimeout>;
type Listener = (id: string | null, from: HoverFrom | null) => void;

export class Hovered {
  id: string | null = null;
  from: HoverFrom | null = null;
  // Whether the pointer is on the list beside the map at all (on a row or between them): the map comes
  // back from a hover's zoom-out once it has left (TripMap).
  onList = false;
  private readonly listeners = new Set<Listener>();
  private readonly presence = new Set<(on: boolean) => void>();
  private readonly query = matchMedia(HOVERS);

  // Whether this pointer event is one that hovers: on a device whose pointer hovers, and not a finger
  // (a touch screen on a laptop).
  hovers(e: PointerEvent): boolean {
    return e.pointerType !== "touch" && this.query.matches;
  }

  set(id: string | null, from: HoverFrom | null) {
    const at = id === null ? null : from;
    if (id === this.id && at === this.from) return;
    this.id = id;
    this.from = at;
    for (const listener of this.listeners) listener(id, at);
  }

  // The pointer left `from`: nothing is hovered, unless the other side has it by now.
  leave(from: HoverFrom) {
    if (this.from === from) this.set(null, null);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // The pointer came onto the list, or left it.
  list(on: boolean) {
    if (on === this.onList) return;
    this.onList = on;
    for (const listener of this.presence) listener(on);
  }

  watchList(listener: (on: boolean) => void): () => void {
    this.presence.add(listener);
    return () => {
      this.presence.delete(listener);
    };
  }
}

// The place whose small card shows at the map's foot while the pointer is on it (features/map.md, "One
// click or tap to an item's card"): once the pointer has rested on its row or its pin, never while it
// sweeps past; kept while the pointer is on its row, its pin or the card itself, so the card can be
// clicked, and gone once it has been off all three a moment. Resting on another one swaps the two in
// place: when this one's moment runs out while the pointer is resting on another, it stays until that
// rest is done (or the pointer moves on), rather than leaving the map's foot empty in between.
export function useHoverCard(hover: Hovered | null): string | null {
  const [shown, setShown] = useState<string | null>(null);
  useEffect(() => {
    if (!hover) return;
    let card: string | null = null;
    // What the pointer is resting on, until its rest is done.
    let resting: string | null = null;
    let rest: Timer | undefined;
    let away: Timer | undefined;
    // The card's moment off it ran out while the pointer was resting on another.
    let due = false;
    const show = (id: string | null) => {
      card = id;
      due = false;
      clearTimeout(away);
      away = undefined;
      setShown(id);
    };
    const stopResting = () => {
      clearTimeout(rest);
      rest = undefined;
      resting = null;
    };
    const update = (id: string | null) => {
      // On it again (its row, its pin, the card): it stays.
      if (id !== null && id === card) {
        stopResting();
        due = false;
        clearTimeout(away);
        away = undefined;
        return;
      }
      if (id !== resting) {
        stopResting();
        // Moved on before that rest was done: the card waiting on it goes now.
        if (due) show(null);
        if (id !== null) {
          resting = id;
          rest = setTimeout(() => {
            stopResting();
            show(id);
          }, REST_MS);
        }
      }
      if (card === null || away !== undefined || due) return;
      away = setTimeout(() => {
        away = undefined;
        if (rest !== undefined) due = true;
        else show(null);
      }, AWAY_MS);
    };
    update(hover.id);
    const off = hover.subscribe((id) => update(id));
    return () => {
      off();
      clearTimeout(rest);
      clearTimeout(away);
    };
  }, [hover]);
  return shown;
}

// The item a pointer event is over, among elements marked with `data-item` inside `root`.
export function itemAt(e: Event, root: Element, selector: string): string | null {
  const target = e.target instanceof Element ? e.target.closest<HTMLElement>(selector) : null;
  return target && root.contains(target) ? (target.dataset["item"] ?? null) : null;
}

// Where `id` is hovered from (its row in the list, itself on the map, or its small card), or null when it
// isn't: drawn again only when that changes for it, not for every other row.
export function useHovering(hover: Hovered | null, id: string): HoverFrom | null {
  const [on, setOn] = useState<HoverFrom | null>(() => (hover?.id === id ? hover.from : null));
  const now = useRef(on);
  useEffect(() => {
    if (!hover) return;
    const update = (h: string | null, from: HoverFrom | null) => {
      const mine = h === id ? from : null;
      if (mine === now.current) return;
      now.current = mine;
      setOn(mine);
    };
    update(hover.id, hover.from);
    return hover.subscribe(update);
  }, [hover, id]);
  return on;
}
